import {
  Box,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import {
  CloudDownloadOutlined,
  Visibility,
  WarningAmberRounded,
  CheckCircle,
  FilterAltOutlined,
} from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { toast } from "react-toastify";

function OrdersReport() {
  const token = localStorage.getItem("token");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [loading, setLoading] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());

  const [searchText, setSearchText] = useState("");

  const [orderReports, setOrderReports] = useState([]);
  const [orderReportsData, setOrderReportsData] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const handleOpenModal = (order) => {
    setSelectedOrder(order);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedOrder(null);
  };

  const normalizeArrivalMeta = (orderObj) => {
    const status = orderObj?.order?.orderArrivalStatus || null;
    const earlyArrival = orderObj?.order?.earlyArrival || null;
    const delayedArrival = orderObj?.order?.delayedArrival || null;

    return {
      status,
      earlyArrival,
      delayedArrival,
    };
  };

  const getDelayedChipProps = (orderObj) => {
    const { status } = normalizeArrivalMeta(orderObj);

    if (status === "DELAYED") {
      return {
        label: "Late",
      };
    }

    return null;
  };

  const getArrivalChipPropsForModal = (orderObj) => {
    const { status, earlyArrival, delayedArrival } = normalizeArrivalMeta(orderObj);

    if (status === "EARLY") {
      return {
        label: `EARLY${earlyArrival ? ` • ${earlyArrival}` : ""}`,
        color: "#15803d",
        bg: "#dcfce7",
        border: "#86efac",
        icon: <CheckCircle sx={{ fontSize: 16 }} />,
      };
    }

    if (status === "DELAYED") {
      return {
        label: `DELAYED${delayedArrival ? ` • ${delayedArrival}` : ""}`,
        color: "#b91c1c",
        bg: "#fee2e2",
        border: "#fca5a5",
        icon: <WarningAmberRounded sx={{ fontSize: 16 }} />,
      };
    }

    return null;
  };

  const getStatusChipStyles = (status) => {
    switch (status) {
      case "COMPLETED":
      case "DELIVERED":
        return {
          bg: "#dcfce7",
          color: "#166534",
        };
      case "CONFIRMED":
        return {
          bg: "#dbeafe",
          color: "#1d4ed8",
        };
      case "CANCELLED":
        return {
          bg: "#fee2e2",
          color: "#b91c1c",
        };
      case "READY-TO-PICK":
        return {
          bg: "#f3e8ff",
          color: "#7e22ce",
        };
      default:
        return {
          bg: "#e2e8f0",
          color: "#334155",
        };
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return "N/A";
    const d = new Date(dateValue);
    return `${d.toLocaleDateString()}, ${d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })}`;
  };

  const getOrderReports = async (download = false) => {
    if (!outletData?._id) return;

    setLoading(true);
    try {
      const startDate = format(selectedStartDate, "yyyy-MM-dd");
      const endDate = format(selectedEndDate, "yyyy-MM-dd");

      const params = {
        startDate: startDate,
        endDate: endDate,
        outlet: outletData._id,
      };

      if (download) params.isDownload = "true";

      const response = await axios.get(`${API.BASE_URL}order/getOrdersForAdmin`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: download ? "blob" : "json",
      });

      if (download) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const selectedOutletName = outletData?.name || outletData?.outletName || "Outlet";
        const fileName = `${selectedOutletName} Orders Report (${startDate} to ${endDate}).xlsx`;

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success("Report Excel file downloaded successfully!", {
          position: "bottom-center",
          autoClose: 3000,
        });

        return;
      }

      const totalRecords = response?.data?.data?.totalOrdersData || [];
      setOrderReportsData(response?.data?.data || null);
      setOrderReports(Array.isArray(totalRecords) ? totalRecords : []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error:", error);
      toast.error(download ? "Failed to download report" : "Failed to fetch report", {
        autoClose: 3000,
      });
      if (!download) setOrderReports([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(orderReports) ? orderReports : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const orderNo = `#${r?.order?.order_sequence || ""}${r?.order?.order_no || ""}`.toLowerCase();
      const name = (r?.order?.kioskUser?.name || r?.order?.user?.name || "").toLowerCase();
      const phone = (r?.order?.kioskUser?.phone || r?.order?.user?.mobile || "").toLowerCase();
      const coupon = (r?.order?.coupon?.couponCode || "").toLowerCase();
      const source = (r?.order?.source || "").toLowerCase();
      const arrivalStatus = (r?.order?.orderArrivalStatus || "").toLowerCase();

      return (
        orderNo.includes(q) ||
        name.includes(q) ||
        phone.includes(q) ||
        coupon.includes(q) ||
        source.includes(q) ||
        arrivalStatus.includes(q)
      );
    });
  }, [orderReports, searchText]);

  const legendTotals = useMemo(() => {
    const rows = Array.isArray(filteredRows) ? filteredRows : [];

    return rows.reduce(
      (acc, r) => {
        const arrivalStatus = r?.order?.orderArrivalStatus;

        acc.totalOrders += 1;
        acc.totalGrand += Number(r?.order?.grandTotal || 0);
        acc.totalTax += Number(r?.order?.totalTaxAmount || 0);
        acc.totalDiscount += Number(r?.order?.totalDiscount || 0);

        if (arrivalStatus === "EARLY") {
          acc.earlyCount += 1;
        } else if (arrivalStatus === "DELAYED") {
          acc.delayedCount += 1;
        }

        return acc;
      },
      {
        totalOrders: 0,
        totalGrand: 0,
        totalTax: 0,
        totalDiscount: 0,
        earlyCount: 0,
        delayedCount: 0,
      }
    );
  }, [filteredRows]);

  const totalPages = Math.ceil((filteredRows?.length || 0) / itemsPerPage) || 1;
  const paginatedRows = filteredRows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    getOrderReports();
  }, []);

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                    Customer Orders Report
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Filter by date, search orders, then view or download the report.
                  </Typography>
                </Box>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${filteredRows?.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                   Start Date
                  </Typography>

                  <DatePicker
                    selected={selectedStartDate}
                    onChange={(date) => {
                      setSelectedStartDate(date);
                      setCurrentPage(1);
                    }}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>
                  
                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                   End Date
                  </Typography>

                  <DatePicker
                    selected={selectedEndDate}
                    onChange={(date) => {
                      setSelectedEndDate(date);
                      setCurrentPage(1);
                    }}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedEndDate ? format(selectedEndDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search Order
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by order no / customer / coupon / arrival status"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => getOrderReports(false)}
                      sx={{
                        height: 52,
                        width: "70%",
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    <CloudDownloadOutlined
                      onClick={() => filteredRows?.length > 0 && getOrderReports(true)}
                      sx={{
                        fontSize: "35px",
                        cursor: filteredRows?.length > 0 ? "pointer" : "not-allowed",
                        color: filteredRows?.length > 0 ? "#003049" : "#94a3b8",
                        display: "flex",
                        alignSelf: "center",
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Orders...</Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No orders to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select a date and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${filteredRows?.length || 0}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Early: ${legendTotals.earlyCount}`}
                          sx={{
                            fontWeight: 800,
                            borderRadius: 2,
                            color: "#166534",
                            backgroundColor: "#dcfce7",
                            border: "1px solid #86efac",
                          }}
                        />
                        <Chip
                          label={`Delayed: ${legendTotals.delayedCount}`}
                          sx={{
                            fontWeight: 800,
                            borderRadius: 2,
                            color: "#b91c1c",
                            backgroundColor: "#fee2e2",
                            border: "1px solid #fca5a5",
                          }}
                        />
                        <Chip
                          label={`Tax: ₹${legendTotals.totalTax.toFixed(2)}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Discount: ₹${legendTotals.totalDiscount.toFixed(2)}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label={`Grand Total: ₹${legendTotals.totalGrand.toFixed(2)}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      maxHeight: "70vh",
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["Order No", "Customer", "Coupon", "Date", "Time", "Amount", "Detail"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={h === "Customer" ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((row, index) => {
                          const orderNo = `#${row?.order?.order_sequence || ""}${row?.order?.order_no || ""}`;
                          const custName = row?.order?.kioskUser
                            ? row?.order?.kioskUser?.name
                            : row?.order?.user?.name;
                          const custPhone = row?.order?.kioskUser
                            ? row?.order?.kioskUser?.phone
                            : row?.order?.user?.mobile;

                          const createdAt = row?.order?.createdAt ? new Date(row.order.createdAt) : null;
                          const delayedChip = getDelayedChipProps(row);

                          return (
                            <TableRow
                              key={orderNo + index}
                              hover
                              sx={{
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 800, color: "#0f172a" }}>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}
                                  >
                                    {orderNo}
                                  </Typography>

                                  {delayedChip && (
                                    <Chip
                                      label="Late"
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "10px",
                                        fontWeight: 800,
                                        borderRadius: "10px",
                                        color: "#b91c1c",
                                        backgroundColor: "#fee2e2",
                                        border: "1px solid #fca5a5",
                                        "& .MuiChip-label": {
                                          px: 0.8,
                                          py: 0,
                                        },
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>

                              <TableCell align="left">
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {custName || "N/A"}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                  {custPhone || "N/A"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.3 }}>
                                  <b>Source:</b> {row?.order?.source || "N/A"}
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {row?.order?.coupon?.couponCode || "N/A"}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {createdAt ? createdAt.toISOString().split("T")[0] : "N/A"}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 700 }}>
                                {createdAt
                                  ? createdAt.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })
                                  : "N/A"}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                ₹{Number(row?.order?.grandTotal || 0).toFixed(2)}
                              </TableCell>

                              <TableCell align="center">
                                <IconButton onClick={() => handleOpenModal(row)}>
                                  <Visibility />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          Order Details
        </DialogTitle>

        <DialogContent dividers>
          {selectedOrder ? (
            <>
              {(() => {
                const arrivalChip = getArrivalChipPropsForModal(selectedOrder);
                const orderStatusStyle = getStatusChipStyles(selectedOrder?.order?.orderStatus);
                const paymentPaid = selectedOrder?.order?.paymentStatus === "PAID";

                return (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        justifyContent="center"
                        sx={{ mb: 1 }}
                      >
                        <Chip
                          label={`Order #${selectedOrder?.order?.order_sequence}${selectedOrder?.order?.order_no}`}
                          sx={{
                            fontWeight: 900,
                            backgroundColor: "#003049",
                            color: "#fff",
                            "& .MuiChip-label": { color: "#fff" },
                          }}
                        />

                        <Chip
                          label={selectedOrder?.order?.orderStatus || "N/A"}
                          sx={{
                            fontWeight: 800,
                            backgroundColor: orderStatusStyle.bg,
                            color: orderStatusStyle.color,
                          }}
                        />

                        <Chip
                          label={selectedOrder?.order?.paymentStatus || "N/A"}
                          sx={{
                            fontWeight: 800,
                            backgroundColor: paymentPaid ? "#dcfce7" : "#fee2e2",
                            color: paymentPaid ? "#166534" : "#b91c1c",
                          }}
                        />

                        {arrivalChip && (
                          <Chip
                            icon={arrivalChip.icon}
                            label={arrivalChip.label}
                            sx={{
                              fontWeight: 800,
                              color: arrivalChip.color,
                              backgroundColor: arrivalChip.bg,
                              border: `1px solid ${arrivalChip.border}`,
                              "& .MuiChip-icon": { color: arrivalChip.color },
                            }}
                          />
                        )}
                      </Stack>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Typography variant="body1">
                        <strong>User Name:</strong>{" "}
                        {selectedOrder?.order?.kioskUser
                          ? selectedOrder?.order?.kioskUser?.name
                          : selectedOrder?.order?.user?.name || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Typography variant="body1">
                        <strong>Mobile Number:</strong>{" "}
                        {selectedOrder?.order?.kioskUser
                          ? selectedOrder?.order?.kioskUser?.phone
                          : selectedOrder?.order?.user?.mobile || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Typography variant="body1">
                        <strong>Coupon Code:</strong>{" "}
                        <span style={{ color: "green", fontWeight: 500 }}>
                          {selectedOrder?.order?.coupon?.couponCode || "N/A"}
                        </span>
                      </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Typography variant="body1">
                        <strong>Order Source:</strong> {selectedOrder?.order?.source || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Typography variant="body1">
                        <strong>Order Type:</strong> {selectedOrder?.order?.ordersType || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <Typography variant="body1">
                        <strong>Date & Time:</strong> {formatDateTime(selectedOrder?.order?.createdAt)}
                      </Typography>
                    </Grid>

                    <Grid item md={4} xs={12}>
                      <Typography variant="body1">
                        <strong>Arrival Status:</strong> {selectedOrder?.order?.orderArrivalStatus || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={4} xs={12}>
                      <Typography variant="body1">
                        <strong>Early By:</strong> {selectedOrder?.order?.earlyArrival || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={4} xs={12}>
                      <Typography variant="body1">
                        <strong>Delayed By:</strong> {selectedOrder?.order?.delayedArrival || "N/A"}
                      </Typography>
                    </Grid>

                    <Grid item md={12} xs={12} sx={{ mt: 1 }}>
                      <Typography variant="h6" gutterBottom textAlign="center">
                        Ordered Items
                      </Typography>

                      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>Product</th>
                            <th style={{ padding: "8px", border: "1px solid #ccc", textAlign: "center" }}>
                              Quantity
                            </th>
                            <th style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }}>
                              Price
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {selectedOrder?.orderItems?.map((item, index) => (
                            <React.Fragment key={index}>
                              <tr>
                                <td style={{ padding: "8px", border: "1px solid #ccc", verticalAlign: "top" }}>
                                  <strong>{item?.product?.name}</strong>
                                  {item?.addons?.length > 0 && (
                                    <ul style={{ paddingLeft: "20px", margin: 0, listStyleType: "disc" }}>
                                      {item.addons.map((addon, i) => (
                                        <li key={i}>{addon?.selectedValue}</li>
                                      ))}
                                    </ul>
                                  )}
                                </td>

                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    textAlign: "center",
                                    verticalAlign: "top",
                                  }}
                                >
                                  {item?.quantity}
                                </td>

                                <td
                                  style={{
                                    padding: "8px",
                                    border: "1px solid #ccc",
                                    textAlign: "right",
                                    verticalAlign: "top",
                                  }}
                                >
                                  <div>
                                    ₹{Number(item?.taxable || 0).toFixed(2)}
                                    <br />
                                    {(item?.addons || []).map((addon, i) => (
                                      <div key={i}>₹{Number(addon?.offerPrice || 0).toFixed(2)}</div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            </React.Fragment>
                          ))}

                          <tr>
                            <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }} colSpan={2}>
                              <strong style={{ marginRight: "30px" }}>Total Tax Amount</strong>
                            </td>
                            <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }}>
                              <strong>₹{Number(selectedOrder?.order?.totalTaxAmount || 0).toFixed(2)}</strong>
                            </td>
                          </tr>

                          <tr>
                            <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }} colSpan={2}>
                              <strong style={{ marginRight: "30px" }}>Total Discount</strong>
                            </td>
                            <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }}>
                              <strong>₹{Number(selectedOrder?.order?.totalDiscount || 0).toFixed(2)}</strong>
                            </td>
                          </tr>

                          <tr>
                            <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }} colSpan={2}>
                              <strong style={{ marginRight: "30px" }}>Order Total</strong>
                            </td>
                            <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "right" }}>
                              <strong>₹{Number(selectedOrder?.order?.grandTotal || 0).toFixed(2)}</strong>
                            </td>
                          </tr>
                        </tbody>
                      </Box>
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <Typography variant="h6" gutterBottom textAlign="center">
                        Order Status Logs
                      </Typography>

                      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>Status</th>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>Outlet</th>
                            <th style={{ padding: "8px", border: "1px solid #ccc" }}>Timestamp</th>
                          </tr>
                        </thead>

                        <tbody>
                          {selectedOrder?.orderStatusLogs?.length > 0 ? (
                            selectedOrder?.orderStatusLogs?.map((log, index) => (
                              <tr key={index}>
                                <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "center" }}>
                                  {typeof log?.orderStatus === "string" ? log.orderStatus : "N/A"}
                                </td>
                                <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "center" }}>
                                  {log?.outlet?.name || "N/A"}
                                </td>
                                <td style={{ padding: "8px", border: "1px solid #ccc", textAlign: "center" }}>
                                  {formatDateTime(log?.createdAt)}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={3}
                                style={{ padding: "12px", border: "1px solid #ccc", textAlign: "center" }}
                              >
                                No logs available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })()}
            </>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default OrdersReport;